/**
 * WeatherTopicPage — /weather/topic/:key
 *
 * Standalone page for each Weather Almanac advisory topic (Planting,
 * Harvest, Fertilizer, Livestock, Gardening, Pests, Allergy, Fishing,
 * Moon-Gardening, Drought, Outdoor Activities, …).
 *
 * Content is fetched via the site-wide AI Topics engine: if a topic
 * doesn't exist yet, `useAiTopic` posts a generation request and polls
 * until it's ready. Every topic gets its own Facebook-style message
 * board.
 */
import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, CloudSun } from "lucide-react";
import EntityMessageBoard from "@/components/EntityMessageBoard";
import AiTopicContent from "@/components/AiTopicContent";
import useAiTopic from "@/hooks/useAiTopic";

/* ─── topic catalog ─────────────────────────────────────────────────
 * Every card on the Weather Almanac maps to a canonical key here so
 * URLs stay stable and the LLM gets a strong context prompt. Add new
 * entries as new advisory cards are introduced.
 */
export const WEATHER_TOPICS = {
  planting:        { title: "Planting Calendar",        context: "When to plant common vegetables, herbs, and flowers; how weather patterns and soil temperature drive planting windows; tips for temperate climates." },
  harvest:         { title: "Harvest Guide",            context: "Which crops are ready to harvest in each season; signs of ripeness; storage tips after picking." },
  fertilizer:      { title: "Fertilizer Timing",        context: "When to fertilize lawns, gardens, and crops based on weather, soil moisture, and growth cycle; risks of over-fertilizing." },
  livestock:       { title: "Livestock Weather Care",   context: "Practical weather-driven care for backyard chickens, goats, cows, horses, and other livestock; extreme heat, cold, and storm precautions." },
  gardening:       { title: "Weather-Smart Gardening",  context: "Weekly gardening tasks aligned to weather: watering, pruning, mulching, pest checks, and seasonal transitions." },
  pests:           { title: "Pest Forecast",            context: "Common garden and household pests; how temperature, humidity, and rainfall influence populations; safe, sustainable control methods." },
  allergy:         { title: "Allergy Forecast",         context: "Pollen, mold, dust, and other airborne allergens by season; how weather and wind affect exposure; symptom mitigation tips." },
  activities:      { title: "Outdoor Activities Today", context: "How weather influences safety and enjoyment of common outdoor activities: hiking, cycling, running, picnics, sports; when to shift plans indoors." },
  fishing:         { title: "Fishing Conditions",       context: "How barometric pressure, moon phase, water temperature, and wind affect fishing success; best times and species-specific tips." },
  moon_gardening:  { title: "Moon Gardening",           context: "Traditional lunar-cycle gardening: which tasks align with waxing vs. waning moons; how modern science evaluates the practice." },
  drought:         { title: "Drought Watch",            context: "How rainfall deficits build into drought conditions; monitoring rainfall over 30/90/365-day windows; water conservation tips." },
  severe:          { title: "Severe Weather Prep",      context: "Preparing for tornadoes, hurricanes, blizzards, ice storms, heat waves, flooding, and other severe events; go-bags and family plans." },
  farmer:          { title: "Farmer's Dashboard",       context: "A one-page overview of weather-driven farm decisions across planting, harvest, fertilizer, and livestock." },
};

export default function WeatherTopicPage() {
  const { key } = useParams();
  const meta    = WEATHER_TOPICS[key] || { title: key?.replaceAll("_", " ") || "Topic", context: "" };
  const title   = meta.title;

  const ai = useAiTopic({
    source:    "weather",
    entityKey: key || "",
    title,
    context:   meta.context,
    deepLink:  `/weather/topic/${key}`,
  });

  const heroPalette = useMemo(
    () => ({ from: "#DBEAFE", to: "#BFDBFE", accent: "#1D4ED8", ink: "#1E3A8A" }),
    [],
  );

  return (
    <main className="max-w-3xl mx-auto px-5 lg:px-8 py-8" data-testid={`weather-topic-${key}`}>
      <nav className="text-xs opacity-70 mb-4" aria-label="Breadcrumb">
        <Link to="/" className="hover:underline">Home</Link>
        <span className="mx-2">/</span>
        <Link to="/weather" className="hover:underline">Weather Almanac</Link>
        <span className="mx-2">/</span>
        <span>{title}</span>
      </nav>

      <div
        className="rounded-2xl p-6 border mb-6"
        style={{
          borderColor: `${heroPalette.accent}33`,
          background: `linear-gradient(135deg, ${heroPalette.from} 0%, ${heroPalette.to} 100%)`,
        }}
      >
        <div
          className="text-xs font-mono uppercase tracking-widest mb-2 flex items-center gap-1"
          style={{ color: heroPalette.ink }}
        >
          <CloudSun className="w-3 h-3" />
          <Link to="/weather" className="hover:underline">Weather Almanac</Link>
          <span className="mx-1 opacity-40">·</span>
          <span>Topic</span>
        </div>
        <h1
          className="font-display leading-[1.05] tracking-tight"
          style={{ color: heroPalette.ink, fontSize: "clamp(2rem, 4.5vw, 3rem)" }}
          data-testid="weather-topic-title"
        >
          {title}
        </h1>
      </div>

      <AiTopicContent
        doc={ai.doc}
        status={ai.status}
        retry={ai.retry}
        title={title}
      />

      <div className="mt-10 pt-6 border-t" style={{ borderColor: "var(--wofy-border)" }}>
        <Link
          to="/weather"
          className="inline-flex items-center gap-2 text-sm font-semibold hover:underline"
          style={{ color: heroPalette.accent }}
          data-testid="weather-topic-back"
        >
          <ArrowLeft className="w-4 h-4" /> Back to the Weather Almanac
        </Link>
      </div>

      <EntityMessageBoard
        entityType="weather"
        entityId={key || "topic"}
        title={`Discuss · ${title}`}
      />
    </main>
  );
}
